import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { runWebsiteDiagnostics } from "../api/diagnosticsApi";
import { DiagnosticsResponse } from "../types/diagnostics";

interface UseRunWebsiteDiagnosticState {
  loading: boolean;
  submittingLeadCapture: boolean;
  report: DiagnosticsResponse | null;
  requiresLeadCapture: boolean;
  reportUrl: string | null;
  websiteUrl: string;
  error: string | null;
  leadCaptured: boolean;
  capturedEmail: string | null;
}

export const useRunWebsiteDiagnostic = () => {
  const [state, setState] = useState<UseRunWebsiteDiagnosticState>({
    loading: false,
    submittingLeadCapture: false,
    report: null,
    requiresLeadCapture: false,
    reportUrl: null,
    websiteUrl: "",
    error: null,
    leadCaptured: false,
    capturedEmail: null,
  });

  // Track analytics event
  const trackEvent = useCallback((eventName: string, data?: Record<string, any>) => {
    // Implement your analytics tracking here (e.g., Google Analytics, Mixpanel, etc.)
    // Example: if you have a global analytics service
    // analytics.track(eventName, data);
  }, []);

  // Initial diagnostic run
  const runDiagnostic = useCallback(async (urlOrQr: string, captchaToken?: string) => {
    if (!urlOrQr.trim()) {
      const errorMsg = "Please enter a valid URL";
      setState((prev) => ({ ...prev, error: errorMsg }));
      toast.error(errorMsg);
      return;
    }

    // Basic URL validation
    let normalizedUrl = urlOrQr.trim();

    if (
      !normalizedUrl.startsWith("http://") &&
      !normalizedUrl.startsWith("https://")
    ) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    // Basic URL validation
    try {
      new URL(normalizedUrl);
    } catch (_) {
      const errorMsg = "Please enter a valid URL format (e.g., https://strivreservices.com)";
      setState((prev) => ({ ...prev, error: errorMsg }));
      toast.error(errorMsg);
      return;
    }

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
      report: null,
      requiresLeadCapture: false,
      reportUrl: null
    }));

    trackEvent("website_diagnostic_started", { url: urlOrQr });

    try {
      const response = await runWebsiteDiagnostics({
        urlOrQr: normalizedUrl,
        ...(captchaToken ? { captchaToken } : {}),
      });

      if (response.requiresLeadCapture) {
        // Lead capture is needed
        setState((prev) => ({
          ...prev,
          loading: false,
          requiresLeadCapture: true,
          websiteUrl: normalizedUrl,
        }));
        trackEvent("lead_capture_required", { url: normalizedUrl });
      } else {
        // We have the report
        setState((prev) => ({
          ...prev,
          loading: false,
          report: response.report || null,
          reportUrl: response.reportUrl || null,
          websiteUrl: normalizedUrl,
          requiresLeadCapture: false,
        }));
        trackEvent("website_diagnostic_completed", {
          url: normalizedUrl,
          overallPercentage: response.report?.overallPercentage,
        });
        toast.success("Website diagnostic completed!");
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Failed to run diagnostics. Please try again.";
      setState((prev) => ({
        ...prev,
        loading: false,
        error: errorMsg,
      }));
      toast.error(errorMsg);
      console.error("Diagnostics error:", errorMsg);
    }
  }, [trackEvent]);

  // Submit lead capture form
  const submitLeadCapture = useCallback(
    async (
      leadData: {
        name: string;
        company: string;
        email: string;
      },
      captchaToken: string
    ) => {
      if (!captchaToken) {
        const errorMsg = "Please complete the CAPTCHA verification.";
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

      setState((prev) => ({
        ...prev,
        submittingLeadCapture: true,
        error: null,
      }));

      try {
        const response = await runWebsiteDiagnostics({
          urlOrQr: state.websiteUrl,
          ...leadData,
          captchaToken,
        });

        trackEvent("lead_capture_submitted", {
          url: state.websiteUrl,
          ...leadData,
        });

        if (response.message === "Processing") {
          setState((prev) => ({
            ...prev,
            submittingLeadCapture: false,
            requiresLeadCapture: false,
            leadCaptured: true,
            capturedEmail: leadData.email,
          }));

          trackEvent("website_diagnostic_queued", {
            url: state.websiteUrl,
            email: leadData.email,
          });

          toast.success(
            "Your report is being generated and will be sent to your email."
          );

          return;
        }

        throw new Error("Unexpected response from server");
      } catch (err) {
        const errorMsg =
          err instanceof Error
            ? err.message
            : "Failed to submit lead capture. Please try again.";

        setState((prev) => ({
          ...prev,
          submittingLeadCapture: false,
          error: errorMsg,
        }));

        toast.error(errorMsg);
        console.error("Lead capture error:", errorMsg);

        throw err;
      }
    },
    [state.websiteUrl, trackEvent]
  );

  // Open report in new tab
  const openFullReport = useCallback(() => {
    if (state.reportUrl) {
      window.open(state.reportUrl, "_blank");
    }
  }, [state.reportUrl]);

  // Reset state
  const reset = useCallback(() => {
    setState({
      loading: false,
      submittingLeadCapture: false,
      report: null,
      requiresLeadCapture: false,
      reportUrl: null,
      websiteUrl: "",
      error: null,
      leadCaptured: false,
      capturedEmail: null,
    });
  }, []);

  return {
    // State
    loading: state.loading,
    submittingLeadCapture: state.submittingLeadCapture,
    report: state.report,
    requiresLeadCapture: state.requiresLeadCapture,
    reportUrl: state.reportUrl,
    websiteUrl: state.websiteUrl,
    error: state.error,
    leadCaptured: state.leadCaptured,
    capturedEmail: state.capturedEmail,

    // Actions
    runDiagnostic,
    submitLeadCapture,
    openFullReport,
    reset,
  };
};
